import { useCallback, useState } from 'react';
import TasksService from '../services/TasksService';
import TeamService from '../services/TeamService';
import { errorText } from '../utils/format';
import useAsync from './useAsync';

export const COLUMNS = [
  { status: 'todo', label: 'To do' },
  { status: 'in_progress', label: 'In progress' },
  { status: 'blocked', label: 'Blocked' },
  { status: 'done', label: 'Done' },
];

/** The shared to-do list: tasks of the owner and the team, with filtering by team member. */
export function useTasksViewModel() {
  const [assignee, setAssignee] = useState('');
  const [showDone, setShowDone] = useState(false);
  const load = useCallback(async () => {
    const [tasks, team] = await Promise.all([TasksService.list({ assignee: assignee || undefined }), TeamService.team()]);
    return { tasks, bots: team?.bots || [] };
  }, [assignee]);
  const { data, loading, error, reload } = useAsync(load);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const save = async (fn) => {
    setSaving(true);
    setSaveError('');
    try {
      await fn();
      await reload({ silent: true });
      return true;
    } catch (err) {
      setSaveError(errorText(err));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const all = data?.tasks || [];
  const visible = all.filter((t) => t.status !== 'cancelled' && (showDone || t.status !== 'done'));
  return {
    tasks: visible,
    openCount: all.filter((t) => ['todo', 'in_progress', 'blocked'].includes(t.status)).length,
    bots: (data?.bots || []).filter((b) => b.active),
    loading,
    error,
    reload,
    assignee,
    setAssignee,
    showDone,
    setShowDone,
    saving,
    saveError,
    clearSaveError: () => setSaveError(''),
    createTask: (task) => save(() => TasksService.create(task)),
    updateTask: (id, fields) => save(() => TasksService.update(id, fields)),
  };
}
